// Makes a React hydration failure SELF-IDENTIFYING in the owned `js_error`
// stream (rule 65: `js_error` → Cloudflare → events_raw is canonical for
// browser errors; rule 34: never a Cloud Run route).
//
// The honest limitation, verified in node_modules on 2026-08-25: Next.js 16
// hardcodes React's `onRecoverableError`
// (`next/dist/client/react-client-callbacks/on-recoverable-error.js`) and its
// signature is `(error) => …` — React's `errorInfo.componentStack` is dropped
// before Next forwards the error to `reportGlobalError` → `window.reportError`.
// The App Router exposes no way to supply our own callback (it is passed
// straight into `hydrateRoot` inside `client/app-index.js`), so the component
// stack is UNREACHABLE from application code. What `reportError` does give us
// is a real `error` event on `window` carrying the Error object — this module
// listens for it, recognises the recoverable-hydration family, and attaches the
// closest available identity: `error.digest`, the containers React regenerated
// during hydration, and the surviving `<template data-dgst>` bailouts.
//
// Rule 47 (fire once): this handler shapes the beacon fields BYTE-IDENTICALLY
// to `GlobalErrorReporter`, so when that later listener reports the same event
// the beacon's own `name|message|stack` fingerprint de-dupes it. Exactly one
// `js_error` leaves the document per occurrence — the enriched one. The error
// object is also annotated with the fields `GlobalErrorReporter` already reads
// (`ishvaramBoundary` / `ishvaramComponent`) so identity survives even if our
// own call is throttled out.
//
// The route and the release SHA need no work here: `reportJsError` already
// stamps `page_location` (scrubbed) and `release` on every beacon.
//
// The evidence is ALSO published on `window` under `HYDRATION_EVIDENCE_KEY`.
// Annotating the Error object alone is not enough for the fastest reader we
// have: Playwright's `pageerror` serialises an Error across CDP as name/message/
// stack only, so `ishvaramComponent` is dropped before the synthetic can see it.
// The synthetic checkout has reproduced this failure every 30 minutes since
// 2026-08-23 — it is the reproducer, and it could still only report the opaque
// number. Same already-scrubbed, already-capped string that leaves in the
// beacon, so this publishes no field the document was not already emitting.

import {
  buildHydrationEvidence,
  collectBailoutTemplates,
  describeElementPath,
  reactRecoverableCode,
} from "@/lib/hydration-error-evidence";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";
// Already in the root client graph (ThemeToggle): no new module ships.
import { THEME_STORAGE_KEY } from "@/lib/theme-contract";

/**
 * Where the last recoverable-hydration evidence is published for in-page readers.
 * @internal
 */
export const HYDRATION_EVIDENCE_KEY = "__ishvaramHydrationEvidence";

/** Bounded witness: a childList-only observer recording REPLACED containers. */
const WITNESS_MAX_PATHS = 8;
// Long enough to cover a late-hydrating boundary (the live #418 fires ~4.4s
// after load), short enough that it is not a lifetime observer on every page.
const WITNESS_WINDOW_MS = 20_000;

let installed = false;
/** `<html lang>` as the edge and the pre-paint lang-sync script left it. */
let documentLang = "";
let observer: MutationObserver | null = null;
let witnessTimer: ReturnType<typeof setTimeout> | null = null;
const regenerated: string[] = [];
const inserted: string[] = [];
const INSERTED_MAX = 3;

/**
 * The injector's identity, PII-safe: tag name plus the HOST of a src/href —
 * never a path or query, which can carry tokens. The EARLIEST insertions in
 * the record stream precede React's root recovery (records preserve order),
 * so the first three name the foreign mutator (extension vs tag gateway vs
 * app script) that regen=head,body alone cannot separate.
 */
function insertedIdentity(node: Node): string | null {
  if (!(node instanceof Element)) return null;
  const tag = node.tagName.toLowerCase();
  const source = node.getAttribute("src") ?? node.getAttribute("href");
  if (!source) return tag === "script" ? "script:inline" : tag;
  try {
    return `${tag}:${new URL(source, window.location.href).host}`;
  } catch {
    return `${tag}:opaque`;
  }
}

function recordInsertions(records: MutationRecord[]): void {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (inserted.length >= INSERTED_MAX) return;
      const identity = insertedIdentity(node);
      if (identity && !inserted.includes(identity)) inserted.push(identity);
    }
  }
}

/**
 * A hydration mismatch makes React drop the server subtree and insert the
 * client one under the SAME parent. That replacement signature names the failing
 * region; append-only or remove-only mutations (normal client rendering) are not
 * it.
 *
 * The removal and the insertion are SEPARATE operations, so they arrive as
 * separate MutationRecords — measured in jsdom: a `removeChild` followed by an
 * `appendChild` on one parent yields 2 records, ZERO of which carry both lists.
 * The first version of this required both on a single record and therefore
 * recorded nothing, ever: the diagnostic built to name the component was
 * structurally incapable of naming one. Accumulate per target across the
 * observation window instead.
 */
const witnessed = new Map<Node, { removed: boolean; added: boolean }>();

/** True once this target has seen BOTH a removal and an addition. */
function isReplaced(record: MutationRecord): boolean {
  const seen = witnessed.get(record.target) ?? { removed: false, added: false };
  seen.removed = seen.removed || record.removedNodes.length > 0;
  seen.added = seen.added || record.addedNodes.length > 0;
  witnessed.set(record.target, seen);
  // A target that only ever grows, or only ever shrinks, is ordinary client
  // rendering — not a regenerated subtree.
  return seen.removed && seen.added;
}

function recordReplacements(records: MutationRecord[]): void {
  recordInsertions(records);
  for (const record of records) {
    if (regenerated.length >= WITNESS_MAX_PATHS) {
      stopWitness();
      return;
    }
    if (!isReplaced(record)) continue;
    const path = describeElementPath(record.target);
    if (path && !regenerated.includes(path)) regenerated.push(path);
  }
}

/**
 * The witness observes REACT'S HYDRATION CONTAINER, which is the whole
 * `document` — verified in next 16.2.11
 * (`next/dist/client/app-index.js`: `const appElement = document;` →
 * `hydrateRoot(appElement, reactEl, …)`). Anything narrower is structurally
 * incapable of witnessing part of what React hydrates, and that is the bug this
 * function had: rooted at `document.body`, a mismatch in `<head>` — the live
 * /free-kundali/ #418, where the Cloudflare tag gateway injects a script into
 * the head AFTER the Worker renders it — produced no record at all, so every
 * run reported the non-finding `regen=none`. The reporter could not see the
 * thing that was mismatching.
 *
 * Widening does mean ordinary head churn (preloads, hoisted styles) is now in
 * scope; `isReplaced` still requires the same target to BOTH lose and gain a
 * child, so append-only asset loading is not recorded as a regeneration.
 */
function startWitness(): void {
  if (observer || typeof MutationObserver === "undefined") return;
  if (typeof document === "undefined") return;
  observer = new MutationObserver(recordReplacements);
  observer.observe(document, { childList: true, subtree: true });
  witnessTimer = setTimeout(stopWitness, WITNESS_WINDOW_MS);
}

function stopWitness(): void {
  witnessed.clear();
  observer?.disconnect();
  observer = null;
  if (witnessTimer !== null) clearTimeout(witnessTimer);
  witnessTimer = null;
}

/** `error.digest` — React copies the server's `data-dgst` onto #419 errors. */
function readDigest(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const digest = (value as { digest?: unknown }).digest;
  return typeof digest === "string" && digest ? digest : undefined;
}

/** Fields `GlobalErrorReporter` already reads off a thrown Error. */
function annotate(value: unknown, boundary: string, component: string): void {
  if (!value || typeof value !== "object") return;
  const owned = value as { ishvaramBoundary?: string; ishvaramComponent?: string };
  owned.ishvaramBoundary = boundary;
  owned.ishvaramComponent = component;
}

// Mirror of GlobalErrorReporter's shaping, including the filename fallback, so
// both listeners produce the same beacon fingerprint for the same event.
function publish(boundary: string, component: string): void {
  try {
    (window as unknown as Record<string, unknown>)[HYDRATION_EVIDENCE_KEY] = {
      boundary,
      component,
    };
  } catch {
    // A frozen or proxied window must not break error reporting.
  }
}

function errorFields(event: ErrorEvent): {
  error_message: string;
  error_name: string;
  error_stack: string;
} {
  const fields = event.error
    ? jsErrorFieldsFromUnknown(event.error)
    : { error_message: event.message || "Uncaught error", error_name: "Error", error_stack: "" };
  return {
    ...fields,
    error_stack:
      fields.error_stack ||
      (event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : ""),
  };
}

/**
 * t-errors-03: a recoverable hydration error that regenerates the root makes
 * React re-apply the root layout's static `<html lang="en">` and drop the
 * `data-theme` the theme bootstrap set, so a /hi/ page lost its Devanagari
 * font stack and screen-reader language, and a dark-mode reader flipped to
 * light. React reports the error after that commit (flushSpawnedWork follows
 * the mutation and layout phases) and never touches those static attributes
 * again, so restoring them here sticks. The theme is read from storage at
 * error time, the same key the bootstrap reads, so a toggle since load holds.
 */
function restoreDocumentState(): void {
  const root = document.documentElement;
  if (documentLang && root.lang !== documentLang) root.lang = documentLang;
  let dark = false;
  try {
    dark = window.localStorage.getItem(THEME_STORAGE_KEY) === "dark";
  } catch {
    // Storage blocked: the bootstrap could not have set dark either.
  }
  if (dark && root.getAttribute("data-theme") !== "dark") root.setAttribute("data-theme", "dark");
}

function onWindowError(event: ErrorEvent): void {
  try {
    const fields = errorFields(event);
    const code = reactRecoverableCode(fields.error_message);
    // Not a recoverable-hydration error → leave it entirely to
    // GlobalErrorReporter. This path must never reshape an unrelated js_error.
    if (!code) return;
    restoreDocumentState();
    // Deliver the mutations the observer has QUEUED but not yet dispatched:
    // the mismatch fires this handler in the same task as the replacement
    // mutations, so without an explicit drain the witness loses exactly the
    // subtree it exists to name — 372/372 live events carried regen=none
    // while the paths sat in this queue and stopWitness() discarded them.
    if (observer) recordReplacements(observer.takeRecords());
    const evidence = buildHydrationEvidence({
      code,
      digest: readDigest(event.error),
      regenerated,
      templates: collectBailoutTemplates(document),
      inserted,
    });
    const boundary = `react_recoverable:${code}`;
    annotate(event.error, boundary, evidence);
    publish(boundary, evidence);
    reportJsError({ ...fields, boundary, component: evidence });
    stopWitness();
  } catch {
    // An error reporter that throws inside window.onerror cascades. Never.
  }
}

/**
 * Install at the earliest point our own bundle can reach — module evaluation of
 * the client component, which precedes `hydrateRoot`, so a mismatch reported
 * during the first commit (before ANY useEffect exists) is still captured.
 * Idempotent: safe under React StrictMode double-invocation and a re-import.
 */
export function installHydrationErrorReporter(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;
  // Module evaluation runs after the inline <head> scripts, so this is the
  // language the document was served and synced with.
  documentLang = document.documentElement.lang;
  window.addEventListener("error", onWindowError);
  startWitness();
}

/**
 * Test-only: drop the listener, the observer, and the witness buffer.
 * @internal
 */
export function resetHydrationErrorReporterForTests(): void {
  if (typeof window !== "undefined") {
    window.removeEventListener("error", onWindowError);
    delete (window as unknown as Record<string, unknown>)[HYDRATION_EVIDENCE_KEY];
  }
  stopWitness();
  regenerated.length = 0;
  inserted.length = 0;
  documentLang = "";
  installed = false;
}
